// The Excel report's layout sheet (the customer's choice, 30-10-2026): the chosen layout's columns in its order, named
// as the layout, after "All data" - the report on screen (ReportGenerator: the same rows, one per diary entry when the
// layout has task fields, empty task rows left out) in All data's look (cellStyle.js): headers by category, the CO
// groups' shades and lines, dates as real dates DD-MM-YYYY, and a task's Done date / Time interval / Planned as in All
// data's task cells (a real date, "TI ...", "P ...") in its status's colour.
// The data is a trial as POST /v1/trials/{ids} returns it (with its relations and diaries).
import ReportGenerator from '../../components/Reports/ReportGenerator';
import { formatReportValue, isMissing } from '../../components/Reports/reportFields';
import { cellStatus } from './cellStatus';
import {
  MIN_DATE_WIDTH, NINE, addHeaderRow, coGroups, groupStarts, setDataCell, setUpSheet, styleCell,
} from './cellStyle';
import { LAYOUT_TAB_COLOUR, NO_TASK_SHADES, ROW_SHADES } from './colours';
import { excelDate, plannedText, timeIntervalCell } from './taskCells';
import { sortTrials } from './workbook';

/** Names the layout sheet may not take (Excel compares sheet names without case). */
export const TAKEN_SHEET_NAMES = ['All data', 'Colors'];
export const FALLBACK_SHEET_NAME = 'Layout';
const MAX_SHEET_NAME = 31;
const INVALID_SHEET_CHARS = /[\\/?*[\]:]/g;

/**
 * The layout sheet's name: the layout's, without : \ / ? * [ ], at most 31 characters, "Layout" when nothing is left;
 * " (2)" when it is a taken name (All data, Colors).
 */
export function layoutSheetName(name, taken = TAKEN_SHEET_NAMES) {
  const clean = String(name ?? '').replace(INVALID_SHEET_CHARS, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_SHEET_NAME).trim()
    || FALLBACK_SHEET_NAME;
  const used = taken.map((n) => n.toLowerCase());
  if (!used.includes(clean.toLowerCase())) return clean;
  const suffix = ' (2)';
  return `${clean.slice(0, MAX_SHEET_NAME - suffix.length).trimEnd()}${suffix}`;
}

// The layout fields' categories (All data's, colours.js); every other field is trial info
const GROUPS = {
  identity: ['trialno', 'customer.customerid', 'project.contract.co', 'PO', 'project_id'],
  people: ['studydirector', 'PI', 'FT', 'pitxt'],
  setup: ['ResAr', 'StOut', 'PlotSize', 'Borders_Sprayed'],
  finish: ['Harvest', 'PegsUp', 'Destruction'],
};

/** A layout field's category (a key of GROUP_FILLS): the task (diary) fields are assessments. */
export function layoutFieldGroup(key) {
  if (key.startsWith('diaries.')) return 'assessments';
  return Object.keys(GROUPS).find((group) => GROUPS[group].includes(key)) ?? 'info';
}

// A task's three date fields: shown as All data shows a task, in the task's status colour
const TASK_DATE_KEYS = ['diaries.donedate', 'diaries.timeinterval', 'diaries.planned_date'];
const YMD = /^\d{4}-\d{2}-\d{2}/;


/**
 * A date as the Date Excel shows as the day the report on screen shows (formatDisplayDate): "2026-05-04" as written,
 * a date-time (e.g. "2026-05-03T22:00:00.000000Z" from the API) on its local day. Null when it is no date.
 */
export function layoutDate(value) {
  const text = String(value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return excelDate(text);
  const d = new Date(text);
  return text === '' || Number.isNaN(d.getTime()) ? null : new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/** The cell value of a layout field for a row: a real date, a number, or the text the report on screen shows. */
export function layoutCellValue(value, column, entry = null) {
  if (column.key === 'diaries.donedate') return entry?.donedate ? layoutDate(entry.donedate) : null;
  if (column.key === 'diaries.timeinterval') return entry ? timeIntervalCell(entry) : null;
  if (column.key === 'diaries.planned_date') {
    const planned = entry ? plannedText(entry) : null;
    return planned === null ? null : `P ${planned}`;
  }
  if (isMissing(value)) return null;
  // A number from the API stays a number in a text field too (CO, Customer No), as in All data
  if (typeof value === 'number' && (column.type === 'text' || column.type === 'number')) return value;
  if (column.type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : String(value);
  }
  // A date (or a date-or-text field holding a date) as a real date; other text as the report shows it
  if ((column.type === 'date' || column.type === 'interval') && YMD.test(String(value))) return layoutDate(value);
  return formatReportValue(value, column);
}

const MIN_WIDTH = 8;
const MAX_WIDTH = 40;
const longestWord = (text) => Math.max(0, ...String(text).split(/\s+/).map((w) => w.length));

/**
 * Adds the layout sheet to an ExcelJS workbook for the trials of POST /v1/trials/{ids}, in All data's order (by CO, then
 * Trial), as on `exportDate`. Returns { name, rows, columns }.
 */
export function addLayoutSheet(workbook, { name, fields, trials, exportDate = new Date() }) {
  const sheetName = layoutSheetName(name);
  const sorted = sortTrials(trials.map((trial) => ({ ...trial, co: trial.project?.contract?.co })));
  const report = ReportGenerator({ trials: sorted, fields, hideEmptyTaskRows: true });
  const { columns, header, rows, rowTrials, rowEntries } = report;

  const sheet = workbook.addWorksheet(sheetName);
  const groups = columns.map((c) => layoutFieldGroup(c.key));
  const starts = groupStarts(groups);
  const trialNoAt = columns.findIndex((c) => c.key === 'trialno');
  setUpSheet(sheet, report.perDiaryEntry ? report.frozenColumns : trialNoAt + 1, LAYOUT_TAB_COLOUR);

  const values = rows.map((row, r) => row.map((value, c) => layoutCellValue(value, columns[c], rowEntries[r])));
  // Each column as wide as its longest value (a date at least MIN_DATE_WIDTH), never narrower than a header word
  const widths = columns.map((column, c) => {
    let longest = 0;
    let date = TASK_DATE_KEYS.includes(column.key);
    for (const row of values) { // a loop, not Math.max(...): a sheet can have tens of thousands of rows
      if (row[c] instanceof Date) date = true;
      else for (const line of String(row[c] ?? '').split('\n')) longest = Math.max(longest, line.length);
    }
    const width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, longestWord(column.label) + 2, longest + 2, date ? MIN_DATE_WIDTH : 0));
    return width === 9 ? NINE : width; // ExcelJS leaves out a width of exactly 9 (cellStyle.js)
  });
  addHeaderRow(sheet, header, { groups, widths });

  const cos = rowTrials.map((t) => sorted[t].co);
  const coRows = coGroups(cos);
  values.forEach((rowValues, r) => {
    const row = sheet.getRow(r + 2);
    const { shade, coStart } = coRows[r];
    const status = rowEntries[r] ? cellStatus(rowEntries[r], exportDate).status : null;
    columns.forEach((column, c) => {
      const cell = row.getCell(c + 1);
      const task = TASK_DATE_KEYS.includes(column.key);
      setDataCell(cell, rowValues[c], { categoryStart: starts[c], coStart, task });
      // A task's dates in its status colour; an empty one a shade lighter, as an empty task cell in All data
      const fallbackFill = task ? NO_TASK_SHADES[shade] : ROW_SHADES[shade];
      styleCell(cell, { status: task && rowValues[c] !== null ? status : null, fallbackFill });
    });
  });
  if (columns.length) {
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(1, rows.length + 1), column: columns.length } };
  }
  return { name: sheetName, rows: rows.length, columns: header };
}
